import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const Probedaten = ({ projectId }) => {
  const [rehearsalData, setRehearsalData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [newEntry, setNewEntry] = useState({
    datum: '',
    wochentag: '',
    anfangszeit: '',
    endzeit: '',
    artDerProbe: '',
    anmerkungen: ''
  });

  const sortByDate = (data) => {
    return [...data].sort((a, b) => {
      const dateA = a.datum.includes('.') 
        ? new Date(a.datum.split('.').reverse().join('-') + 'T00:00:00') 
        : new Date(a.datum + 'T00:00:00');
      const dateB = b.datum.includes('.') 
        ? new Date(b.datum.split('.').reverse().join('-') + 'T00:00:00') 
        : new Date(b.datum + 'T00:00:00');
      return dateA - dateB;
    });
  };

  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        const savedData = localStorage.getItem(`probedaten_${projectId}`);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            if (parsedData && parsedData.length > 0) {
              setRehearsalData(sortByDate(parsedData));
            }
          } catch (error) {
            console.error('Fehler beim Laden der Probendaten:', error);
          }
        }
      }
    };
    loadSavedData();
  }, [projectId]);

  useEffect(() => {
    if (projectId && rehearsalData.length > 0) {
      localStorage.setItem(`probedaten_${projectId}`, JSON.stringify(rehearsalData));
    }
  }, [rehearsalData, projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'datum' && { wochentag: new Date(value).toLocaleDateString('de-DE', { weekday: 'long' }) })
    }));
  };

  const handleCellEdit = (id, field, value) => {
    setRehearsalData(prev => {
      const updatedData = prev.map(entry => {
        if (entry.id === id) {
          if (field === 'datum') {
            return {
              ...entry,
              [field]: value,
              wochentag: new Date(value).toLocaleDateString('de-DE', { weekday: 'long' })
            };
          }
          return { ...entry, [field]: value };
        }
        return entry;
      });
      return sortByDate(updatedData);
    });
    setEditingCell(null);
  };

  const addEntry = () => {
    if (newEntry.datum) {
      const entryToAdd = { ...newEntry, id: Date.now() };
      console.log('Adding new entry:', entryToAdd);
      setRehearsalData(prev => {
        const newData = sortByDate([...prev, entryToAdd]);
        console.log('New sorted data:', newData);
        return newData;
      });
      setNewEntry({
        datum: '',
        wochentag: '',
        anfangszeit: '',
        endzeit: '',
        artDerProbe: '',
        anmerkungen: ''
      });
    }
  };

  const deleteEntry = (id) => {
    if (window.confirm('Möchten Sie diesen Probentermin wirklich löschen?')) {
      console.log('Deleting entry with id:', id);
      setRehearsalData(prev => {
        const filteredData = prev.filter(entry => entry.id !== id);
  
        if (filteredData.length === 0) {
          localStorage.removeItem(`probedaten_${projectId}`);
        } else {
          localStorage.setItem(`probedaten_${projectId}`, JSON.stringify(filteredData));
        }
  
        console.log('Data after deletion:', filteredData);
        return filteredData;
      });
    }
  };

  const exportToCSV = () => {
    const csv = Papa.unparse(rehearsalData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'probedaten.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const data = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        header: 'A'
      });

      const formattedData = data
        .slice(1)
        .filter(row => row.A && !row.A.includes('#'))
        .map(row => {
          let datum = row.A || '';
          if (datum.includes('.')) {
            datum = datum.split('.').reverse().join('-');
          }
          return {
            id: Date.now() + Math.random(),
            datum: datum,
            wochentag: row.B || '',
            anfangszeit: row.C || '',
            endzeit: row.D || '',
            artDerProbe: row.E || '',
            anmerkungen: row.F || ''
          };
        });

      if (formattedData.length === 0) {
        alert('Keine gültigen Daten gefunden. Bitte überprüfen Sie die Spaltenbezeichnungen.');
        return;
      }

      setRehearsalData(prev => {
        const newData = [...prev, ...formattedData];
        return newData.sort((a, b) => a.datum.localeCompare(b.datum));
      });

      alert(`${formattedData.length} Probentermine wurden erfolgreich importiert.`);
    } catch (error) {
      console.error('Fehler beim Datei-Import:', error);
      alert('Fehler beim Import der Datei. Bitte überprüfen Sie das Format.');
    }
    e.target.value = '';
  };

  return (
    <div className="p-4 md:p-6 w-full">
      <h2 className="text-2xl font-bold mb-4">Probedaten</h2>

      {/* Eingabeformular - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <input
          type="date"
          name="datum"
          value={newEntry.datum}
          onChange={handleInputChange}
          placeholder="Datum"
          className="border p-2 rounded w-full"
        />
        <input
          type="text"
          name="wochentag"
          value={newEntry.wochentag}
          placeholder="Wochentag"
          readOnly
          className="border p-2 bg-gray-100 rounded w-full"
        />
        <input
          type="time"
          name="anfangszeit"
          value={newEntry.anfangszeit}
          onChange={handleInputChange}
          placeholder="Anfangszeit"
          className="border p-2 rounded w-full"
        />
        <input
          type="time"
          name="endzeit"
          value={newEntry.endzeit}
          onChange={handleInputChange}
          placeholder="Endzeit"
          className="border p-2 rounded w-full"
        />
        <input
          type="text"
          name="artDerProbe"
          value={newEntry.artDerProbe}
          onChange={handleInputChange}
          placeholder="Art der Probe"
          className="border p-2 rounded w-full"
        />
        <input
          type="text"
          name="anmerkungen"
          value={newEntry.anmerkungen}
          onChange={handleInputChange}
          placeholder="Anmerkungen"
          className="border p-2 rounded w-full"
        />
        <button
          onClick={addEntry}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 w-full md:col-span-2 lg:col-span-1"
          disabled={!newEntry.datum}
        >
          Eintrag hinzufügen
        </button>
      </div>

      {/* Import/Export Buttons */}
      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
          id="csvUpload"
        />
        <label 
          htmlFor="csvUpload" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600 text-center"
        >
          Excel importieren
        </label>
        <button
          onClick={exportToCSV}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Als Excel exportieren
        </button>
      </div>

     {/* Tabelle mit STICKY horizontalem Scroll */}
      <div style={{ 
        width: '100%', 
        overflowX: 'auto',
        overflowY: 'visible',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'white',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        maxHeight: '600px',
        position: 'relative'
      }}>
        <table style={{ 
          minWidth: '1400px',
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white'
        }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#e5e7eb', zIndex: 10 }}>
            <tr>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '110px', fontSize: '14px' }}>Datum</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '110px', fontSize: '14px' }}>Wochentag</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '90px', fontSize: '14px' }}>Anfang</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '90px', fontSize: '14px' }}>Ende</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '180px', fontSize: '14px' }}>Art der Probe</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '250px', fontSize: '14px' }}>Anmerkungen</th>
              <th style={{ border: '1px solid #d1d5db', padding: '12px 8px', minWidth: '100px', fontSize: '14px' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {rehearsalData.map((entry) => (
              <tr key={entry.id} style={{ backgroundColor: 'white' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                <td 
                  style={{ border: '1px solid #e5e7eb', padding: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => setEditingCell({ id: entry.id, field: 'datum' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'datum' ? (
                    <input
                      type="date"
                      value={entry.datum}
                      onChange={(e) => handleCellEdit(entry.id, 'datum', e.target.value)}
                      autoFocus
                      onBlur={() => setEditingCell(null)}
                      style={{ width: '100%', border: 'none', outline: '2px solid #3b82f6', padding: '4px' }}
                    />
                  ) : entry.datum}
                </td>
                <td style={{ border: '1px solid #e5e7eb', padding: '8px', whiteSpace: 'nowrap' }}>{entry.wochentag}</td>
                <td 
                  style={{ border: '1px solid #e5e7eb', padding: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => setEditingCell({ id: entry.id, field: 'anfangszeit' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'anfangszeit' ? (
                    <input
                      type="time"
                      value={entry.anfangszeit}
                      onChange={(e) => handleCellEdit(entry.id, 'anfangszeit', e.target.value)}
                      autoFocus
                      onBlur={() => setEditingCell(null)}
                      style={{ width: '100%', border: 'none', outline: '2px solid #3b82f6', padding: '4px' }}
                    />
                  ) : entry.anfangszeit}
                </td>
                <td 
                  style={{ border: '1px solid #e5e7eb', padding: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => setEditingCell({ id: entry.id, field: 'endzeit' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'endzeit' ? (
                    <input
                      type="time"
                      value={entry.endzeit}
                      onChange={(e) => handleCellEdit(entry.id, 'endzeit', e.target.value)}
                      autoFocus
                      onBlur={() => setEditingCell(null)}
                      style={{ width: '100%', border: 'none', outline: '2px solid #3b82f6', padding: '4px' }}
                    />
                  ) : entry.endzeit}
                </td>
                <td 
                  style={{ border: '1px solid #e5e7eb', padding: '8px', cursor: 'pointer' }}
                  onClick={() => setEditingCell({ id: entry.id, field: 'artDerProbe' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'artDerProbe' ? (
                    <input
                      type="text"
                      value={entry.artDerProbe}
                      onChange={(e) => handleCellEdit(entry.id, 'artDerProbe', e.target.value)}
                      autoFocus
                      onBlur={() => setEditingCell(null)}
                      style={{ width: '100%', border: 'none', outline: '2px solid #3b82f6', padding: '4px' }}
                    />
                  ) : entry.artDerProbe}
                </td>
                <td 
                  style={{ border: '1px solid #e5e7eb', padding: '8px', cursor: 'pointer' }}
                  onClick={() => setEditingCell({ id: entry.id, field: 'anmerkungen' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'anmerkungen' ? (
                    <input
                      type="text"
                      value={entry.anmerkungen}
                      onChange={(e) => handleCellEdit(entry.id, 'anmerkungen', e.target.value)}
                      autoFocus
                      onBlur={() => setEditingCell(null)}
                      style={{ width: '100%', border: 'none', outline: '2px solid #3b82f6', padding: '4px' }}
                    />
                  ) : entry.anmerkungen}
                </td>
                <td style={{ border: '1px solid #e5e7eb', padding: '8px', textAlign: 'center' }}>
                  <button
                    onClick={() => deleteEntry(entry.id)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px', fontStyle: 'italic' }}>
        💡 Tipp: Tabelle ist horizontal scrollbar - wischen Sie nach links/rechts um alle Spalten zu sehen.
      </p>
    </div>
  );
};

export default Probedaten;
